import * as signalR from '@microsoft/signalr';
import env from 'react-dotenv';

/**
 * Singletons SignalR — hubs eBCall.
 *
 * - /hubs/queue : file d'attente temps réel (QueueSnapshot, TicketCalled…)
 * - /hubs/stats : compteurs superviseur (StatsCounterUpdated…)
 *
 * Payloads en clair (pas de chiffrement AES sur SignalR).
 * L'appartenance aux groupes est perdue à chaque reconnexion → re-join
 * automatique dans onreconnected. Dedup par eventId (fenêtre 500).
 */

type Handler = (payload: any) => void;

const SEEN_MAX = 500;
const seenIds = new Set<string>();

/** Dedup par eventId — un événement peut être rejoué après reconnexion. */
export function isDuplicate(eventId?: string): boolean {
    if (!eventId) return false;
    if (seenIds.has(eventId)) return true;
    seenIds.add(eventId);
    if (seenIds.size > SEEN_MAX) {
        const first = seenIds.values().next().value;
        if (first !== undefined) seenIds.delete(first);
    }
    return false;
}

export type HubScope = { codeAbonne: string; codeFiliale?: string; codeS?: string };

export type HubStatus = 'connected' | 'reconnecting' | 'closed' | 'off';

interface HubDef {
    path: 'hubs/queue' | 'hubs/stats';
    joinMethod: string;
    leaveMethod: string;
    joinArgs: (s: HubScope) => any[];
}

const HUBS: Record<'queue' | 'stats', HubDef> = {
    queue: {
        path: 'hubs/queue',
        joinMethod: 'JoinQueue',
        leaveMethod: 'LeaveQueue',
        joinArgs: (s) => [s.codeAbonne, s.codeFiliale, s.codeS],
    },
    stats: {
        path: 'hubs/stats',
        joinMethod: 'JoinStats',
        leaveMethod: 'LeaveStats',
        joinArgs: (s) => [s.codeAbonne, s.codeFiliale],
    },
};

class HubManager {
    private conn?: signalR.HubConnection;
    private handlers = new Map<string, Set<Handler>>();
    private wired = new Set<string>();
    private statusCbs = new Set<(s: HubStatus) => void>();
    private scope?: HubScope;
    private refs = 0;
    private started = false;

    constructor(private def: HubDef) { }

    /** Connexion partagée — un seul HubConnection par hub, ref-counté. */
    async acquire(getJwt: () => string, scope: HubScope): Promise<void> {
        this.refs++;
        this.scope = scope;
        if (!this.conn) {
            this.conn = new signalR.HubConnectionBuilder()
                .withUrl(env.API_BASE_URL + this.def.path, {
                    accessTokenFactory: getJwt,
                })
                .withAutomaticReconnect()
                .build();

            this.conn.onreconnecting(() => this.emitStatus('reconnecting'));
            // Groupes perdus à la reconnexion → re-join systématique
            this.conn.onreconnected(async () => {
                this.emitStatus('connected');
                await this.join().catch(() => this.emitStatus('reconnecting'));
            });
            this.conn.onclose(() => this.emitStatus('closed'));
            // Handlers déjà enregistrés avant acquire()
            this.wireHandlers(this.conn);
        }        if (!this.started) {
            this.started = true;
            await this.conn.start();
            await this.join();
            this.emitStatus('connected');
        }
    }

    /** Relâche la connexion — arrêt quand plus aucun consommateur. */
    async release(): Promise<void> {
        this.refs = Math.max(0, this.refs - 1);
        if (this.refs > 0 || !this.conn) return;
        await this.leave().catch(() => { });
        await this.conn.stop().catch(() => { });
        this.started = false;
        this.conn = undefined;
        this.emitStatus('off');
    }

    /** Nouvelle tentative après épuisement de withAutomaticReconnect. */
    async reconnect(): Promise<void> {
        if (!this.conn) return;
        try {
            await this.conn.start();
            await this.join();
            this.emitStatus('connected');
        } catch {
            this.emitStatus('closed');
        }
    }

    private async join() {
        if (!this.conn || !this.scope) return;
        await this.conn.invoke(this.def.joinMethod, ...this.def.joinArgs(this.scope));
    }

    private async leave() {
        if (!this.conn || !this.scope) return;
        await this.conn.invoke(this.def.leaveMethod, ...this.def.joinArgs(this.scope));
    }

    /** Abonnement à un événement du hub — retourne un unsubscriber. */
    on(event: string, cb: Handler): () => void {
        if (!this.handlers.has(event)) this.handlers.set(event, new Set());
        this.handlers.get(event)!.add(cb);
        // Connexion déjà vivante → brancher l'événement une seule fois
        if (this.conn && !this.wired.has(event)) {
            this.conn.on(event, (p: any) => this.dispatch(event, p));
            this.wired.add(event);
        }
        return () => this.handlers.get(event)?.delete(cb);
    }

    /** Réenregistre les handlers sur la connexion fraîche. */
    private wireHandlers(conn: signalR.HubConnection) {
        this.wired.clear();
        this.handlers.forEach((_, event) => {
            conn.on(event, (p: any) => this.dispatch(event, p));
            this.wired.add(event);
        });
    }

    private dispatch(event: string, payload: any) {
        if (isDuplicate(payload?.eventId)) return;
        this.handlers.get(event)?.forEach((cb) => cb(payload));
    }

    onStatus(cb: (s: HubStatus) => void): () => void {
        this.statusCbs.add(cb);
        return () => this.statusCbs.delete(cb);
    }

    private emitStatus(s: HubStatus) {
        this.statusCbs.forEach((cb) => cb(s));
    }
}

export const queueHub = new HubManager(HUBS.queue);
export const statsHub = new HubManager(HUBS.stats);
